/**
 * ClockNode - outputs trigger events at regular intervals
 *
 * Pure scheduler. Outputs trigger events with time and rate.
 * Connect to PatternPlayerNode for pattern traversal.
 *
 * Endpoints:
 * - rate (io): musical rate ('4n', '8n', '16n', etc.)
 * - trigger (output): event with time and rate
 * - start, stop (input): control triggers
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { ParamKeys, params, TriggerKeys, triggers } from '@nodes/TypedEndpoints';
import { Dependencies } from '@nodes/Dependencies';
import { NodeDef } from '@runtime/IRuntime';

import { Payload } from '@graph/Payload';
import type { IEndpoint } from '@runtime/IEndpoint';

export type RateNotation = '1n' | '2n' | '4n' | '8n' | '16n' | '32n' | '1m' | '2m' | '4m';
export type Rate = RateNotation | number;

// PPQ = 48 (48 ticks = 1 quarter note)
const NOTATION_TO_TICKS: Record<RateNotation, number> = {
  '1n': 192,
  '2n': 96,
  '4n': 48,
  '8n': 24,
  '16n': 12,
  '32n': 6,
  '1m': 192,
  '2m': 384,
  '4m': 768,
};

export function rateToNotation(rate: Rate): string {
  if (typeof rate === 'number') return `${rate}i`;
  // Check if it's a numeric string (tick value)
  const num = parseInt(rate, 10);
  if (!isNaN(num)) return `${num}i`;
  // Otherwise it's notation like '4n'
  return rate;
}

export function rateToTicks(rate: Rate): number {
  if (typeof rate === 'number') return rate;
  // Check if it's a numeric string (tick value)
  const num = parseInt(rate, 10);
  if (!isNaN(num)) return num;
  // Otherwise it's notation like '4n'
  return NOTATION_TO_TICKS[rate as RateNotation] || 24;
}

// Endpoint configurations
const endpoints = {
  ...params({
    rate: { options: [6, 12, 16, 24, 48] as const, value: 24 },
    syncToTransport: {
      label: 'sync', info: 'When true, clock will start and stop when transport starts and stops',
      dataType: 'boolean', value: true
    },
    running: { dataType: 'boolean', value: false }
  }),
  ...triggers({
    tick: { primary: true, isOutput: true, allowInput: false, allowOutput: false },
    start: { },
    stop: { },
  })
};

export class ClockNode extends BaseNode<typeof endpoints> {

  private scheduledEventId: number | null = null;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.updateTriggerVisibility();
    this.schedule();
    this.subscribeToTransport()
  }

  dispose(): void {
    // Clear transport event FIRST — before touching params which may throw
    // if the definition has already been torn down during project switch
    this.clear();
    this.unsubscribeFromTransport();
    super.dispose();
  }

  onParamChanged(payload: Payload, endpointId: ParamKeys<typeof endpoints>): void {
    if (endpointId === 'rate') {
      this.schedule()
    } else if (endpointId === 'syncToTransport') {
      this.updateTriggerVisibility();
      if (payload.value) {
        this.params.running.set(this.transport.state === 'started')
      }
    }
  }

  private updateTriggerVisibility(): void {
    // Disable start/stop triggers when syncToTransport is enabled
    this.setEndpointDisabled('start', this.params.syncToTransport.value);
    this.setEndpointDisabled('stop', this.params.syncToTransport.value);
  }

  handleTrigger(payload: Payload, endpointId: TriggerKeys<typeof endpoints>, source: IEndpoint): void {
    if (endpointId === 'start') {
      this.running = true;
    }
    if (endpointId === 'stop') {
      this.running = false;
    }
  }


  clear() {
    if (this.scheduledEventId !== null) {
      this.transport.clear(this.scheduledEventId);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  schedule(): void {
    this.clear()

    const rate = this.params.rate.value as Rate;
    const notation = rateToNotation(rate);

    this.scheduledEventId = this.transport.scheduleRepeat(
      (time: number) => {
        if (this.params.running.value) {
          this.tick(time)
        }
      },
      notation,
      '0i'  // Start at tick 0
    );
  }

  stop(): void {
    this.running = false;
  }

  get running(): boolean {
    return this.params.running.value;
  }
  set running(running: boolean) {
    this.params.running.set(running)
  }

  // ─────────────────────────────────────────────────────────────
  // Private
  // ─────────────────────────────────────────────────────────────

  private tick(time: number): void {
    const rate = this.params.rate.value as Rate;
    const rateTicks = rateToTicks(rate);
    this.triggers.tick.emit({
      time,
      rate: rateTicks
    })
    this.logTrace('tick', rate, time)
  }

  private subscribeToTransport(): void {
    this.transport.on('start', this.boundStart);
    this.transport.on('stop', this.boundStop);
    this.transport.on('pause', this.boundStop);
  }

  private unsubscribeFromTransport(): void {
    this.transport.off('start', this.boundStart);
    this.transport.off('stop', this.boundStop);
    this.transport.off('pause', this.boundStop);
  }

  private boundStart = () => { if (this.params.syncToTransport.value) { this.running = true } };
  private boundStop = () => { if (this.params.syncToTransport.value) { this.running = false } };
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const clockNodeDef: NodeDef<ClockNode> = {
  type: 'Clock',
  info: 'Outputs trigger events at a musical rate. Connect to PatternPlayer for sequencing.',
  domain: Domain.Data,
  endpoints,
  ctor: ClockNode
};
